import { ParticipantRoleGuard } from 'app/shared/guards/participant-role.guard';
import { NgModule, ErrorHandler } from '@angular/core';
import { AppComponent } from './app.component';
import { AuthService } from 'app/auth/auth.service';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HeaderModule } from './header/header.module';
import { UserRoleGuard } from './shared/guards/user-role.guard';
import { UtilsService } from './shared/utils.service';
import { FileService } from './shared/file.service';
import { DomainFrameworkService } from './shared/domain-framework.service';
import { SidenavModule } from './sidenav/sidenav.module';
import { FooterModule } from './footer/footer.module';
import { AppRoutesModule } from './app.routes';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { SvgIconService } from './shared/svg-icon/svg-icon.service';
import { SvgIconModule } from './shared/svg-icon/svg-icon.module';
import { CygovErrorHandler } from './shared/cygov-error-handler/cygov-error-handler.service';
import { ToastrModule, GlobalConfig } from 'ngx-toastr';
import { LoggerModule, NgxLoggerLevel, INGXLoggerConfig } from 'ngx-logger';
import { environment } from 'environments/environment';
import { JiraConfirmationModalComponent } from './shared/jira-confirmation-modal/jira-confirmation-modal.component';
import { ScanIntegrationServiceModalComponent } from './shared/scan-integration-service-modal/scan-integration-service-modal.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { WelcomeGuideModalComponent } from 'app/shared/welcome-guide-modal/welcome-guide-modal.component';
import { RiskRegisterService } from './risk-register/risk-register.service';
import { HitrustConfirmationModalComponent } from './shared/hitrust-confirmation-modal/hitrust-confirmation-modal.component';
import { NotAuthorizedComponent } from './not-authorized/not-authorized.component';
import { UsersSettingsModule } from './users-settings/users-settings.module';
import { LargeNumberShort } from 'app/shared/pipes/large-number-short.pipe';
import { ClientLambdaService } from 'app/shared/client-lambda.service';
import { AdminRoleGuard } from './shared/guards/admin-role.guard';
import { CollectionService } from './collection/collection.service';
import { FrameworkModalComponent } from './shared/framework-modal/framework-modal.component';
import { OnboardingModule } from './onboarding/onboarding.module';
import { CUSTOMAPIService } from './custom-api.service';
import { UserPermissionModule } from './shared/user-permission/user-permission.module';
import { ESEService } from './shared/ese.service';
import { DefaultTagsService } from './shared/default-tags.service';
import { NgxPaginationModule } from 'ngx-pagination';
import { PipesModule } from './shared/pipes/pipes.module';
import { CygovLoaderModule } from './shared/cygov-loader/cygov-loader.module';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { AddMultiRiskFrameworkModalModule } from './shared/add-multi-risk-framework-modal/add-multi-risk-framework-modal.module';
import { AddNewCustomFrameworkModule } from './shared/add-new-custom-framework/add-new-custom-framework.module';
import { AddParticipantModalModule } from './shared/add-participant-modal/add-participant-modal.module';
import { AddUserModalModule } from './shared/add-user-modal/add-user-modal.module';
import { CheckboxModule } from './shared/checkbox/checkbox.module';
import { ConfirmationModalModule } from './shared/confirmation-modal/confirmation-modal.module';
import { CriticalAlertsModule } from './shared/critical-alerts/critical-alerts.module';
import { SelectModule } from './shared/select/select.module';
import { ToggleButtonModule } from './shared/toggle-button/toggle-button.module';
import { ScoreService } from './shared/score.service';

export function CygovErrorHandlerProvider() {
  return { provide: ErrorHandler, useClass: CygovErrorHandler };
}

const toastConfig: Partial<GlobalConfig> = {
  timeOut: 5000,
  closeButton: true,
  positionClass: 'toast-top-right',
  enableHtml: true,
  progressBar: false,
  progressAnimation: 'increasing',
  newestOnTop: true,
  preventDuplicates: true,
  countDuplicates: true,
  resetTimeoutOnDuplicate: true,
};

const loggerConfig: INGXLoggerConfig = {
  level: environment.debug ? NgxLoggerLevel.LOG : NgxLoggerLevel.OFF,
  serverLogLevel: NgxLoggerLevel.OFF,
};

@NgModule({
  declarations: [
    AppComponent,
    PageNotFoundComponent,
    JiraConfirmationModalComponent,
    ScanIntegrationServiceModalComponent,
    WelcomeGuideModalComponent,
    HitrustConfirmationModalComponent,
    NotAuthorizedComponent,
    FrameworkModalComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutesModule,
    BrowserAnimationsModule,
    NgbModule,
    ToastrModule.forRoot(toastConfig),
    HeaderModule,
    SidenavModule,
    FooterModule,
    SvgIconModule,
    LoggerModule.forRoot(loggerConfig),
    ReactiveFormsModule,
    FormsModule,
    UsersSettingsModule,
    OnboardingModule,
    UserPermissionModule,
    FormsModule,
    NgxPaginationModule,
    PipesModule,
    HeaderModule,
    SvgIconModule,
    NgbModule,
    CygovLoaderModule,
    InfiniteScrollModule,
    AddUserModalModule,
    AddParticipantModalModule,
    AddNewCustomFrameworkModule,
    AddMultiRiskFrameworkModalModule,
    CriticalAlertsModule,
    ToggleButtonModule,
    CheckboxModule,
    SelectModule,
    ConfirmationModalModule,
  ],
  providers: [
    AuthService,
    UserRoleGuard,
    AdminRoleGuard,
    ParticipantRoleGuard,
    UtilsService,
    FileService,
    CollectionService,
    DomainFrameworkService,
    SvgIconService,
    CygovErrorHandlerProvider(),
    RiskRegisterService,
    LargeNumberShort,
    ClientLambdaService,
    CUSTOMAPIService,
    ESEService,
    provideHttpClient(withInterceptorsFromDi()),
    DefaultTagsService,
    ScoreService,
  ],
})
export class AppModule {}
